"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getNodeByKey, type LexicalNode } from "lexical";
import type { ImageBlockNode } from "../../../kernel/nodes/ImageBlockNode";
import {
  LEXICAL_IMAGE_BLOCK_TYPE,
  type ImageAlignment,
  type ImageCropRectNorm,
} from "../../../kernel/nodes/imageBlock.types";
import { CroppedImageView } from "./CroppedImageView";
import { ImageCropDialog } from "./ImageCropDialog";
import { ImageSelectPopover } from "./ImageSelectPopover";
import type { AnchorRect } from "./ImageSelectPopover";
import {
  IconCrop,
  IconDownload,
  IconLayoutAlignCenter,
  IconLayoutAlignLeft,
  IconLayoutAlignRight,
  IconPhotoPlus,
  IconTextCaption,
  IconTrash,
} from "@tabler/icons-react";
import { Button, Input, TextField, Tooltip } from "@heroui/react";

const DISPLAY_WIDTH_MIN = 20;
const DISPLAY_WIDTH_MAX = 100;

function $asImageBlockNode(node: LexicalNode | null): ImageBlockNode | null {
  if (node === null || node.getType() !== LEXICAL_IMAGE_BLOCK_TYPE) return null;
  return node as ImageBlockNode;
}

function downloadImageFile(src: string) {
  const a = document.createElement("a");
  a.href = src;
  a.download = "image";
  a.rel = "noopener noreferrer";
  a.target = "_blank";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function alignmentToLabel(alignment: ImageAlignment): string {
  if (alignment === "left") return "居左";
  if (alignment === "right") return "居右";
  return "居中";
}

export function ImageBlockDecorator({
  nodeKey,
  src,
  alt,
  caption,
  alignment,
  openPickerOnMount,
  widthPercent,
  cropNorm,
}: {
  nodeKey: string;
  src: string;
  alt: string;
  caption: string;
  alignment: ImageAlignment;
  openPickerOnMount: boolean;
  widthPercent: number;
  cropNorm: ImageCropRectNorm | null;
}) {
  const [editor] = useLexicalComposerContext();
  const [isEditable, setIsEditable] = React.useState(() => editor.isEditable());
  React.useLayoutEffect(() => {
    setIsEditable(editor.isEditable());
    return editor.registerEditableListener((next) => setIsEditable(next));
  }, [editor]);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const columnRef = React.useRef<HTMLSpanElement | null>(null);

  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [anchorRect, setAnchorRect] = React.useState<AnchorRect>(null);
  const [isHovering, setIsHovering] = React.useState(false);

  const [alignPickerOpen, setAlignPickerOpen] = React.useState(false);
  const [cropOpen, setCropOpen] = React.useState(false);
  const [captionEditing, setCaptionEditing] = React.useState(false);
  const [captionDraft, setCaptionDraft] = React.useState(caption);
  const captionInputRef = React.useRef<HTMLInputElement | null>(null);

  const [resizePointerDown, setResizePointerDown] = React.useState(false);
  const [dragWidthPercent, setDragWidthPercent] = React.useState<number | null>(null);

  React.useEffect(() => {
    setCaptionDraft(caption);
  }, [caption]);

  React.useEffect(() => {
    if (!captionEditing) return;
    const input = captionInputRef.current;
    if (!input) return;
    requestAnimationFrame(() => {
      input.focus();
      const hasText = captionDraft.trim().length > 0;
      const pos = hasText ? input.value.length : 0;
      input.setSelectionRange(pos, pos);
    });
  }, [captionDraft, captionEditing]);

  const effectiveWidthPercent = dragWidthPercent ?? widthPercent;

  const showChrome =
    Boolean(src) &&
    isEditable &&
    (isHovering || resizePointerDown || alignPickerOpen || cropOpen || captionEditing);

  const openPicker = React.useCallback(() => {
    if (!isEditable) return;
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchorRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    setPickerOpen(true);
  }, [isEditable]);

  React.useEffect(() => {
    if (!openPickerOnMount) return;
    if (!isEditable) return;
    openPicker();
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      const img = $asImageBlockNode(node);
      if (img) img.setOpenPickerOnMount(false);
    });
  }, [editor, isEditable, nodeKey, openPicker, openPickerOnMount]);

  const setAlignment = React.useCallback(
    (next: ImageAlignment) => {
      if (!isEditable) return;
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        const img = $asImageBlockNode(node);
        if (img) img.setAlignment(next);
      });
      setAlignPickerOpen(false);
    },
    [editor, isEditable, nodeKey],
  );

  const setSrc = React.useCallback(
    (nextSrc: string) => {
      if (!isEditable) return;
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        const img = $asImageBlockNode(node);
        if (img) img.setSrc(nextSrc);
      });
    },
    [editor, isEditable, nodeKey],
  );

  const setCropNorm = React.useCallback(
    (next: ImageCropRectNorm | null) => {
      if (!isEditable) return;
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        const img = $asImageBlockNode(node);
        if (img) img.setCropNorm(next);
      });
    },
    [editor, isEditable, nodeKey],
  );

  const download = React.useCallback(() => {
    if (!isEditable) return;
    if (!src) return;
    void downloadImageFile(src);
  }, [isEditable, src]);

  const removeBlock = React.useCallback(() => {
    if (!isEditable) return;
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (node) node.remove();
    });
  }, [editor, isEditable, nodeKey]);

  const justifyClass = alignment === "left" ? "justify-start" : alignment === "right" ? "justify-end" : "justify-center";

  /** 延后到下一帧再打开，避免与文档捕获阶段 pointerdown（关闭菜单等）同一手势冲突导致「一开即关」 */
  const requestOpenCropDialog = React.useCallback(() => {
    setAlignPickerOpen(false);
    setCaptionEditing(false);
    requestAnimationFrame(() => setCropOpen(true));
  }, []);

  const commitCaption = React.useCallback(() => {
    if (!isEditable) return;
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      const img = $asImageBlockNode(node);
      if (img) img.setCaption(captionDraft.trim());
    });
  }, [captionDraft, editor, isEditable, nodeKey]);

  const handleChromeLeave = React.useCallback(
    (e: Pick<React.PointerEvent<HTMLElement>, "currentTarget" | "relatedTarget">) => {
      if (resizePointerDown) return;
      const rel = e.relatedTarget;
      if (rel instanceof Node && e.currentTarget.contains(rel)) return;
      setIsHovering(false);
      setAlignPickerOpen(false);
    },
    [resizePointerDown],
  );

  const startResize = React.useCallback(
    (side: "left" | "right") => (e: React.PointerEvent) => {
      if (!isEditable || !src) return;
      e.preventDefault();
      e.stopPropagation();
      const row = columnRef.current?.parentElement;
      if (!row) return;
      const containerW = row.getBoundingClientRect().width;
      if (containerW <= 0) return;

      const startX = e.clientX;
      const startPct = widthPercent;
      setResizePointerDown(true);
      setDragWidthPercent(startPct);

      let lastPct = startPct;
      const clamp = (v: number) =>
        Math.min(DISPLAY_WIDTH_MAX, Math.max(DISPLAY_WIDTH_MIN, Math.round(v * 10) / 10));

      const onMove = (ev: PointerEvent) => {
        const dx = ev.clientX - startX;
        const dPct = (dx / containerW) * 100;
        lastPct = clamp(side === "right" ? startPct + dPct : startPct - dPct);
        setDragWidthPercent(lastPct);
      };

      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        editor.update(() => {
          const node = $getNodeByKey(nodeKey);
          const img = $asImageBlockNode(node);
          if (img) img.setWidthPercent(lastPct);
        });
        setResizePointerDown(false);
        setDragWidthPercent(null);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [editor, isEditable, nodeKey, src, widthPercent],
  );

  if (!src) {
    return (
      <span className="relative block w-full">
        <button
          ref={triggerRef}
          type="button"
          data-image-picker-trigger="true"
          className="flex w-full items-center gap-2 rounded-lg border border-divider bg-default-100/80 px-4 py-3 transition-colors hover:bg-default-200/80"
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          onClick={() => {
            openPicker();
          }}
          aria-label="添加图片"
          disabled={!isEditable}
        >
          <IconPhotoPlus className="size-4 text-muted" />
          <span className="text-sm font-medium">添加一张图片</span>
        </button>

        <ImageSelectPopover
          open={pickerOpen && isEditable}
          onOpenChange={setPickerOpen}
          anchorRect={anchorRect}
          onPick={(nextSrc) => {
            setSrc(nextSrc);
          }}
          defaultTab="gallery"
        />
      </span>
    );
  }

  return (
    <span className="relative block w-full">
      {/* hover 挂在整行 flex 上：图片列居中且仅占 width% 时，两侧留白仍算「在块上」，否则 mouseenter 进不了窄列 */}
      <span
        className={`flex w-full ${justifyClass}`}
        onPointerEnter={() => setIsHovering(true)}
        onPointerLeave={handleChromeLeave}
      >
        <span
          ref={columnRef}
          className="relative isolate z-[1] min-w-[140px] touch-none flex flex-col"
          style={{ width: `${effectiveWidthPercent}%` }}
        >
          <CroppedImageView src={src} alt={alt} cropNorm={cropNorm} />

          {showChrome ? (
            <>
              {/* 左右内侧竖向手柄：灰底白描边 */}
              <button
                type="button"
                aria-label="拖动调整宽度（左侧）"
                className="absolute left-1 top-1/2 z-30 h-14 w-1.5 -translate-y-1/2 cursor-ew-resize rounded-sm border border-white bg-default-500/80 shadow-sm hover:bg-default-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                onPointerDown={startResize("left")}
              />
              <button
                type="button"
                aria-label="拖动调整宽度（右侧）"
                className="absolute right-1 top-1/2 z-30 h-14 w-1.5 -translate-y-1/2 cursor-ew-resize rounded-sm border border-white bg-default-500/80 shadow-sm hover:bg-default-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                onPointerDown={startResize("right")}
              />

              {/* 右上角工具条 */}
              <span className="absolute right-1 top-1 z-30">
                <span className="inline-flex items-center gap-0.5 rounded-lg border border-divider bg-surface/95 px-1 py-1 shadow-sm backdrop-blur-sm">
                  <span className="relative">
                    <Tooltip>
                      <Tooltip.Trigger>
                        <Button
                          type="button"
                          variant="ghost"
                          isIconOnly
                          size="sm"
                          onMouseDown={(e) => e.preventDefault()}
                          onPress={() => {
                            setCaptionEditing(false);
                            setAlignPickerOpen((v) => !v);
                          }}
                          className="min-h-8 min-w-8 text-foreground"
                          aria-label="图片位置"
                        >
                          {alignment === "left" ? (
                            <IconLayoutAlignLeft className="size-4 text-foreground" />
                          ) : alignment === "right" ? (
                            <IconLayoutAlignRight className="size-4 text-foreground" />
                          ) : (
                            <IconLayoutAlignCenter className="size-4 text-foreground" />
                          )}
                        </Button>
                      </Tooltip.Trigger>
                      <Tooltip.Content placement="top" offset={6}>
                        位置：{alignmentToLabel(alignment)}
                      </Tooltip.Content>
                    </Tooltip>

                    {alignPickerOpen && (
                      <span className="absolute right-0 top-full z-30 mt-1 rounded-xl border border-divider bg-surface p-2 shadow-lg">
                        <span className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            isIconOnly
                            size="sm"
                            onMouseDown={(e) => e.preventDefault()}
                            onPress={() => setAlignment("left")}
                            aria-label="居左"
                            className="min-h-8 min-w-8"
                          >
                            <IconLayoutAlignLeft className="size-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            isIconOnly
                            size="sm"
                            onMouseDown={(e) => e.preventDefault()}
                            onPress={() => setAlignment("center")}
                            aria-label="居中"
                            className="min-h-8 min-w-8"
                          >
                            <IconLayoutAlignCenter className="size-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            isIconOnly
                            size="sm"
                            onMouseDown={(e) => e.preventDefault()}
                            onPress={() => setAlignment("right")}
                            aria-label="居右"
                            className="min-h-8 min-w-8"
                          >
                            <IconLayoutAlignRight className="size-4" />
                          </Button>
                        </span>
                      </span>
                    )}
                  </span>

                  <Tooltip>
                    <Tooltip.Trigger>
                      <Button
                        type="button"
                        variant="ghost"
                        isIconOnly
                        size="sm"
                        onMouseDown={(e) => e.preventDefault()}
                        onPress={() => {
                          requestOpenCropDialog();
                        }}
                        className="min-h-8 min-w-8 text-foreground"
                        aria-label="裁切"
                      >
                        <IconCrop className="size-4" />
                      </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Content placement="top" offset={6}>
                      裁切
                    </Tooltip.Content>
                  </Tooltip>

                  <Tooltip>
                    <Tooltip.Trigger>
                      <Button
                        type="button"
                        variant="ghost"
                        isIconOnly
                        size="sm"
                        onMouseDown={(e) => e.preventDefault()}
                        onPress={() => {
                          setCaptionDraft(caption);
                          setCaptionEditing(true);
                          setAlignPickerOpen(false);
                          setCropOpen(false);
                        }}
                        className="min-h-8 min-w-8"
                        aria-label="图片说明"
                      >
                        <IconTextCaption className="size-4" />
                      </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Content placement="top" offset={6}>
                      说明
                    </Tooltip.Content>
                  </Tooltip>

                  <Tooltip>
                    <Tooltip.Trigger>
                      <Button
                        type="button"
                        variant="ghost"
                        isIconOnly
                        size="sm"
                        onMouseDown={(e) => e.preventDefault()}
                        onPress={() => {
                          setAlignPickerOpen(false);
                          setCaptionEditing(false);
                          setCropOpen(false);
                          removeBlock();
                        }}
                        className="min-h-8 min-w-8"
                        aria-label="删除"
                      >
                        <IconTrash className="size-4" />
                      </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Content placement="top" offset={6}>
                      删除
                    </Tooltip.Content>
                  </Tooltip>

                  <Tooltip>
                    <Tooltip.Trigger>
                      <Button
                        type="button"
                        variant="ghost"
                        isIconOnly
                        size="sm"
                        onMouseDown={(e) => e.preventDefault()}
                        onPress={() => download()}
                        className="min-h-8 min-w-8"
                        aria-label="下载"
                      >
                        <IconDownload className="size-4" />
                      </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Content placement="top" offset={6}>
                      下载
                    </Tooltip.Content>
                  </Tooltip>
                </span>
              </span>
            </>
          ) : null}

          {captionEditing && (
            <span className="mt-2 block min-h-7 w-full px-1">
              <TextField className="w-full">
                <Input
                  ref={captionInputRef}
                  value={captionDraft}
                  onChange={(e) => setCaptionDraft(e.target.value)}
                  onBlur={() => {
                    commitCaption();
                    setCaptionEditing(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      commitCaption();
                      setCaptionEditing(false);
                    }
                    if (e.key === "Escape") {
                      setCaptionDraft(caption);
                      setCaptionEditing(false);
                    }
                  }}
                  placeholder="添加图片说明"
                  className="h-7 border-0 bg-transparent p-0 text-sm leading-7 text-muted shadow-none outline-none ring-0 focus-visible:ring-0"
                />
              </TextField>
            </span>
          )}

          {!captionEditing && caption.trim() && (
            <span className="mt-2 block min-h-7 w-full px-1 text-sm leading-7 text-muted">{caption}</span>
          )}
        </span>
      </span>

      {typeof document !== "undefined"
        ? createPortal(
            <ImageCropDialog
              open={cropOpen}
              onOpenChange={setCropOpen}
              imageSrc={src}
              initialCropNorm={cropNorm}
              onConfirm={(norm) => {
                setCropNorm(norm);
              }}
            />,
            document.body,
          )
        : null}
    </span>
  );
}
